<script setup>
import { HDivider, HRow } from '@pixul/hephaestus';
import ButtonTooltip      from '@/components/Buttons/ButtonTooltip.vue';
</script>

<template>
  <a
    class='padding-14'
    :href='link'
    @click='markAsRead'
  >
    <HRow
      align-items='center'
      gap='14'
      justify-content='space-between'
    >
      <span :class="!notification.read && bold ? 'text-bold' : ''">{{ text }}</span>
      <HRow
        align-items='center'
        gap='7'
      >
        <span class='notification-line__date text-s text-grey-500'>{{ date }}</span>
        <ButtonTooltip
          v-if='!notification.read'
          icon='circle'
          :icon-size='10'
          plain
          position='bottom-right'
          small
          :tooltip="$t('notifications.buttons.markAsRead')"
          variant='circular'
          @click.prevent.stop='markAsRead'
        />
      </HRow>
    </HRow>
  </a>
  <HDivider v-if='showDivider' />
</template>

<script>
import { mapStores }    from 'pinia';
import { useUserStore } from '@/store/user';
import dayjs            from 'dayjs';

import relativeTime            from 'dayjs/plugin/relativeTime';
import { useLanguageStore }    from '@/store/language';
import { useFormatDate }       from '@/composables/formatText.js';
import { getFirstAndLastName } from '@/composables/formatData.js';

dayjs.extend(relativeTime);

export default {
  name : 'NotificationLine',

  props : {
    notification : {
      type    : Object,
      default : () => {}
    },

    showDivider : {
      type    : Boolean,
      default : false
    },

    bold : {
      type    : Boolean,
      default : false
    }
  },

  emits : ['markAsRead'],

  data() {

    return {
      date : ''
    };
  },

  computed : {
    ...mapStores(useUserStore, useLanguageStore),

    notificationFolderId() {

      return this.notification.notification.data.dossier?.id ? this.notification.notification.data.dossier.id : undefined;
    },

    notificationFolderName() {

      if (this.notification.notification.data.dossier?.name) {

        return this.notification.notification.data.dossier.name;
      }

      return this.notificationFolderId;
    },

    link() {

      const type = this.notification.notification.type;

      if (type === 'user_created_comms') {

        return this.$router.resolve({
          name   : `admin-users-relations-details`,
          params : {
            tenant : this.userStore.tenant,
            id     : this.notification.notification.data.user.id
          }
        }).href;
      }

      let tab = 'information';

      if (type === 'third_party_invoice_validated') {
        tab = 'invoices';
      }

      if (type === 'proforma_to_bill' || type === 'invoice_uploaded' || type === 'invoice_refused') {
        tab = 'commissions';
      }

      if (type === 'reminder_triggered' || type === 'task_assigned' || type === 'task_status_changed') {
        tab = 'tasks';
      }

      if (this.notificationFolderId) {

        return this.$router.resolve({
          name   : `folder-details-${ tab }`,
          params : {
            tenant   : this.userStore.tenant,
            folderId : this.notificationFolderId
          }
        }).href;
      }

      return null;
    },

    text() {

      const type = this.notification.notification.type;
      let data = { folderName : this.notificationFolderName };

      switch (type) {
        case 'dossier_assignment_changed' :

          data.user = this.notification.notification.data.newAssigned;
          break;

        case 'dossier_status_changed' :

          const status = this.$t(`folders.status.${ this.notification.notification.data.dossier?.status }`);
          data.status = status.toLowerCase();
          break;

        case 'proforma_to_bill' :
        case 'third_party_invoice_validated' :

          data.id = this.notification.notification.id;
          break;

        case 'invoice_paid' :
          data.amount = this.notification.notification.data.invoice.ttcAmount;
          break;

        case 'invoice_refused' :
          data.invoiceId = this.notification.notification.externalId;
          break;

        case 'reminder_triggered' :
          data = { label : this.notification.notification.data.reminder.task.label };
          break;

        case 'user_created_comms' :
          data = { name : getFirstAndLastName(this.notification.notification.data.user) };
          break;
      }

      return this.$t(`notifications.texts.${ type }`, data);
    }
  },

  watch : {
    'languageStore.language'() {

      this.date = this.getDate();
    }
  },

  created() {

    this.date = this.getDate();
  },

  methods : {
    getDate() {

      const date = this.notification.createdAt;
      const isToday = dayjs().isSame(dayjs(date), 'date');

      if (isToday) {

        return dayjs(date).from(dayjs());
      }

      return useFormatDate(this.notification.createdAt);
    },

    markAsRead() {

      if (this.notification.read) {

        return;
      }

      this.$emit('markAsRead', this.notification.id);
    }
  }
};
</script>

<style rel='stylesheet/scss' lang='scss' scoped>
a {
  cursor: pointer;

  &:hover {
    background: var(--grey-200)
  }
}

.notification-line__date {
  white-space: nowrap;
}
</style>
